import React, { useState, useMemo } from 'react';
import { useFormik } from 'formik';

import { CSSObjectWithLabel } from 'react-select';

import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { isValidDate } from '@/utils/date.utils';
import { initialValues, validationSchema } from './fieldsRules';
import { CANCEL_TYPE } from '@/config/reasonTypes.config';
import LinesCount from '@/components/common/ModalWindow/LinesCount/LinesCount';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';

interface ICancelContractProps {
  onClose: () => void;
  cancelContractLines: (reasonCode: string, cancelDate: any, comment: string) => void;
  selectedContractLinesCount: number;
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  isLoadingReasonCodes?: boolean;
}

export default function CancelContractReasonModal({
  cancelContractLines,
  onClose,
  selectedContractLinesCount,
  reasonCodes,
  reasonTypes,
  isLoadingReasonCodes
}: ICancelContractProps) {
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const { values, setFieldValue, errors, isValid, setFieldTouched, touched } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => { },
    validateOnMount: true
  });

  const isFutureDate = useMemo(() => {
    if (!values.cancellationDate) return false;
    const selected = new Date(values.cancellationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selected > today;
  }, [values.cancellationDate]);

  if (confirmed) {
    return (
      /* eslint-disable max-len */
      <AgConfirmationModal
        isOpen={true}
        title="You are about to Cancel this Contract/Contract Line"
        onClose={onClose}
        onConfirm={() => {
          cancelContractLines(values.cancelReason, values.cancellationDate, values.cancelComment);
        }}
        contentStyle={{ width: 600 }}
      >
        Please confirm to proceed ahead. Cancelling means you will no longer be able to edit the Contract/Contract Line.
      </AgConfirmationModal>
      /* eslint-enable max-len */
    );
  }

  return (
    <AgModalWindow
      title="Cancel Reason"
      isOpen={true}
      onClose={onClose}
      contentStyle={{ width: 340 }}
    >
      <LinesCount className="mb-10">
        {`${selectedContractLinesCount} Lines Selected`}
      </LinesCount>

      <div className="mb-15">
        <ModalFormDropdown<ReasonType>
          id="cancelReasonType"
          labelTitle="Reason Type"
          optionsData={reasonTypes}
          getOptionLabel={(option) => option.formattedType}
          getOptionValue={(option) => option.type}
          disabled
          defaultValue={reasonTypes.find((el) => el.type === CANCEL_TYPE)}
        />
      </div>
      <div className="mb-15">
        <ModalFormDropdown<ReasonCode>
          id="cancelReason"
          labelTitle="Reason Name"
          requiredIcon={true}
          isSearchable={true}
          optionsData={reasonCodes}
          placeholder="Select or type to search"
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.code}
          customStyles={{
            menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
          }}
          menuPortalTarget={document.querySelector('body')}
          handleChange={(newValue) => {
            setFieldTouched('cancelReason', true, false);
            setFieldValue('cancelReason', newValue?.code);
          }}
          validationMessage={
            (errors?.cancelReason && touched.cancelReason) ? errors.cancelReason as string : undefined
          }
          isLoading={isLoadingReasonCodes}
          disabled={isLoadingReasonCodes}
        />
      </div>

      <div className="mb-15">
        <ModalFormDatepicker
          id="cancellationDate"
          labelTitle="Cancellation Date"
          requiredIcon={true}
          onChange={({ value }) => {
            setFieldTouched('cancellationDate', true, false);
            setFieldValue('cancellationDate', value);
          }}
          value={isValidDate(values.cancellationDate) ? new Date(values.cancellationDate) : undefined}
          validationMessage={
            (errors?.cancellationDate && touched.cancellationDate) ? errors.cancellationDate as string : undefined
          }
        />
        {isFutureDate && (
          <div style={{
            marginTop: 6,
            padding: '10px 12px',
            backgroundColor: '#FFF3CD',
            border: '1px solid #FFECB5',
            borderRadius: 4,
            fontSize: 12,
            lineHeight: '18px',
            color: '#664D03',
          }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Is there any further billing still to be raised on this contract?</div>
            <div style={{ marginBottom: 4 }}>
              <strong>No</strong> — all billing is complete → use <strong>Cancel</strong>
            </div>
            <div>
              <strong>Yes</strong>
              — billing must continue to the legal end of contract → use <strong>Do Not Renew (DNR)</strong>
            </div>
          </div>
        )}
      </div>

      <div>
        <ModalFormTextarea
          id="cancelComment"
          labelTitle="Comment"
          requiredIcon={true}
          placeholder="Enter short comments"
          onChange={(event) => {
            setFieldTouched('cancelComment', true, false);
            setFieldValue('cancelComment', event.target.value);
          }}
          validationMessage={
            (errors?.cancelComment && touched.cancelComment) ? errors.cancelComment as string : undefined
          }
        />
      </div>

      <ActionButtonsWrapper
        leftButton={(
          <Button
            buttonStyleType={ButtonStyleType.SECONDARY}
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
        rightButton={(
          <Button
            disabled={!isValid}
            buttonStyleType={ButtonStyleType.PRIMARY}
            onClick={() => setConfirmed(true)}
          >
            Save
          </Button>
        )}
      />
    </AgModalWindow>
  );
}
